/**
 * @file    DrawerContext.tsx
 * @scope   STATE
 * @summary Shared drawer state exposed to the view sections, removing prop drilling.
 * @consumes Nothing — the value is assembled by DiyDrawer and passed to the provider.
 * @produces `DrawerProvider`, `useDrawerContext`
 * @sideEffects None — this is a transport layer only.
 * @notes WHY a context and not a store slice: the values are per-drawer-instance and short-lived,
 *        so persisting them in the global Zustand store would leak drawer state across widgets.
 *        The value MUST be memoized by the provider (DiyDrawer does it with `useMemo`): it used to
 *        be rebuilt on every render, which re-rendered every consumer section for nothing.
 */
import React, { createContext, useContext } from 'react';

export interface DrawerContextValue {
  /** Widget currently being edited (global store mirror). */
  customWidget: any;
  /** Global store writer, used to publish widget mutations. */
  setState: (partial: any) => void;
  /** True while chart metadata is still being fetched. */
  loading: boolean;
  /** Chart option currently selected in the chart-type dropdown. */
  currentAlias: any;
  /** True when the selected chart belongs to the Enterprise world. */
  isDiyWidgetEngine: boolean;
  /**
   * ENTERPRISE-ONLY: chart type the Enterprise widget renders through, `undefined` for legacy
   * charts. Branch on this (not on the boolean) when behaviour differs per Enterprise chart type.
   */
  enterpriseChartType?: { id: string; optionValue: string; component: string };
  /**
   * ENGINE-ONLY: main-tab layout declared by the active subtype descriptor (`hostLayout`),
   * `undefined` for legacy charts. Sections read this instead of branching on the boolean.
   * WHY structural type: keeps this state file free of engine imports.
   */
  engineLayout?: {
    showTrend: boolean;
    showLegacyOptions: boolean;
    showLegacyFilters: boolean;
    mainSection?: string;
  };
  /** Resolves the options lookup key for an alias (handles child charts). */
  currentOptionsAlias: (alias?: string) => string | undefined;
}

const DrawerContext = createContext<DrawerContextValue | null>(null);

export const DrawerProvider: React.FC<{ value: DrawerContextValue; children: React.ReactNode }> = ({
  value,
  children,
}) => <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;

/**
 * Reads the drawer context.
 *
 * @throws When used outside a `DrawerProvider`, which always signals a wiring mistake.
 */
export function useDrawerContext(): DrawerContextValue {
  const context = useContext(DrawerContext);
  if (!context) throw new Error('useDrawerContext must be used inside a DrawerProvider');
  return context;
}

export default DrawerContext;
